import cx from 'classnames';
import React, { useCallback, useContext } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mutate } from 'swr';

import QuotationStatusLabel from './QuotationStatusLabel';
import useCurrencyFormatter from '../../../../Components/Context/Hooks/useCurrencyFormatter';
import useFeatureFlag, { FEATURE_FLAGS } from '../../../../Components/Context/Hooks/useFeatureFlag';
import Box from '../../../../Components/UI/Box';
import { DealsInfo } from '../../../../Types/api';
import { Deal } from '../../../../Types/deal';
import { Quotation as IQuotation, CUSTOMER_PORTAL_STATUS } from '../../../../Types/quotation';
import { MENUS } from '../../../../Utils/Menus';
import { EVENTS, track } from '../../../../Utils/analytics';
import { captureException } from '../../../../Utils/sentry';
import useGlobalContext, { DealContext } from '../../Context';
import styles from '../styles.css';

interface QuotationProps {
  deal: Deal;
  quotation: IQuotation;
  onClick?: (quotation: IQuotation) => void;
}

const Quotation: React.FC<QuotationProps> = ({ deal, quotation, onClick }) => {
  const { token } = useParams<HomeRouteProps>();
  const { quotation: { id, customerPortalStatus } = { id: null, customerPortalStatus: null } } =
    useContext(DealContext);
  const { handleCloseMenu, openMenu } = useGlobalContext();
  const { variation: showAmounts } = useFeatureFlag(FEATURE_FLAGS.SHOW_TOTAL_AMOUNTS);
  const formatter = useCurrencyFormatter(quotation.total.taxInclusive.currency);

  const handleClick = useCallback(() => {
    track(EVENTS.SELECTED_QUOTATION, { quotation: quotation.id });

    mutate(
      '/deals.info',
      (data?: DealsInfo) => {
        const currentQuotation = data?.data.quotations.find((quotation) => quotation.id === id);
        if (currentQuotation && currentQuotation.customerPortalStatus !== CUSTOMER_PORTAL_STATUS.SIGNED) {
          currentQuotation.customerPortalStatus = CUSTOMER_PORTAL_STATUS.VIEWED;
        }

        return data;
      },
      false,
    ).catch(captureException);

    if (customerPortalStatus === CUSTOMER_PORTAL_STATUS.SIGNED && openMenu?.menu === MENUS.SIGN) {
      handleCloseMenu!();
    }

    if (onClick) {
      onClick(quotation);
    }
  }, [quotation, customerPortalStatus, openMenu, onClick, id, handleCloseMenu]);

  const isSelected = quotation.id === id;

  return (
    <Box
      element={isSelected ? 'div' : Link}
      to={`/${token || ''}/${deal.id}/${quotation.id}`}
      onClick={isSelected ? undefined : handleClick}
      className={cx(styles['sub-item'], { [styles['selected']]: isSelected })}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
    >
      <Box display="flex" flexDirection="column">
        <h4 className={styles['title']}>{quotation.name}</h4>
        {showAmounts && (
          <span className={styles['amount']}>{formatter.format(quotation.total.taxInclusive.amount)}</span>
        )}
      </Box>
      <QuotationStatusLabel status={quotation.customerPortalStatus} marginLeft={2} />
    </Box>
  );
};

export default Quotation;
